import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    untracked
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import {
    ApplicationPartner,
    ApplicationPartnerTypeEnum,
    ApplicationUser
} from '@tran-monorepo/interfaces/applications';
import { PeopleSelectComponent } from '../../form';
import { CheckboxComponent } from '../../form/checkbox';
import { ApplicationDetailDashboardBoxComponent } from '../application-detail-dashboard-box/application-detail-dashboard-box.component';
import { ApplicationDetailPeopleComponent } from '../application-detail-people/application-detail-people.component';
import { ApplicationResponsibilitiesForm } from '../application-detail.form';

@Component({
    selector: 'tran-application-responsibilities',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ApplicationDetailDashboardBoxComponent,
        SiFormItemComponent,
        TranslateModule,
        CheckboxComponent,
        ApplicationDetailPeopleComponent,
        PeopleSelectComponent
    ],
    templateUrl: './application-responsibilities.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationResponsibilitiesComponent {
    public readonly partners = input<ApplicationPartner[]>([]);
    public readonly businessResponsible = input<ApplicationUser>();
    public readonly technicalResponsible = input<ApplicationUser>();
    public readonly commercialResponsible = input<ApplicationUser>();
    public readonly heading = input<string>(
        'components.application-detail-dashboard.application-responsibilities.heading'
    );
    public readonly readonly = input(false, { transform: booleanAttribute });
    public readonly isLoading = model<boolean>(false);
    public readonly form = input<ApplicationResponsibilitiesForm | null>(null);

    protected readonly localForm: ApplicationResponsibilitiesForm = new FormGroup({
        learningAndGrowthPartner: new FormControl<ApplicationUser | null>(null),
        workersCouncil: new FormControl<ApplicationUser | null>(null),
        additionalPartners: new FormControl<ApplicationUser[] | null>(null),
        businessResponsible: new FormControl<ApplicationUser | null>(null),
        technicalResponsible: new FormControl<ApplicationUser | null>(null),
        commercialResponsible: new FormControl<ApplicationUser | null>(null)
    }) as ApplicationResponsibilitiesForm;

    protected readonly activeForm = computed(() => this.form() ?? this.localForm);

    constructor() {
        this.effectPatchFormValues();
    }

    private effectPatchFormValues(): void {
        effect(() => {
            const activeForm = this.activeForm();
            const partners = this.partners();
            const businessResponsible = this.businessResponsible();
            const technicalResponsible = this.technicalResponsible();
            const commercialResponsible = this.commercialResponsible();

            if (activeForm) {
                untracked(() => {
                    activeForm.patchValue({
                        learningAndGrowthPartner: this.getSinglePartnerByType(
                            partners,
                            ApplicationPartnerTypeEnum.LearningGrowthPartner
                        ),
                        workersCouncil: this.getSinglePartnerByType(partners, ApplicationPartnerTypeEnum.Betriebsrat),
                        additionalPartners: this.getMultiPartnersByType(partners, [
                            ApplicationPartnerTypeEnum.Other
                        ]) as ApplicationUser[] | null,
                        businessResponsible,
                        technicalResponsible,
                        commercialResponsible
                    });
                });
            }
        });
    }

    private getSinglePartnerByType(
        partners: ApplicationPartner[],
        type: ApplicationPartnerTypeEnum
    ): ApplicationUser | null {
        return partners.find(partner => partner.partnerType === type) as ApplicationUser | null;
    }

    private getMultiPartnersByType(
        partners: ApplicationPartner[],
        types: ApplicationPartnerTypeEnum[]
    ): ApplicationUser[] {
        return partners
            .filter(partner => types.includes(partner.partnerType))
            .map(partner => partner as ApplicationUser);
    }
}
